import React, { useRef } from 'react';
import TextField from '@mui/material/TextField';
import { InputProps } from '@mui/material/Input';
import SearchIcon from '@mui/icons-material/Search';
import useDictionary from '@ui/hooks/useDictionary';
import InputAdornmentComponent from '../Input-adornment/Input-adornment.component';
import PopperComponent from '../Popper/Popper.component';
import { IProps } from './IProps';
import { variants } from '../Popper/variants.enum';

const Component: React.ForwardRefRenderFunction<unknown, IProps> = ({
    lang,
    description,
    disabled,
    error,
    errorMessage,
    helperText,
    id,
    isSearch,
    label,
    multiline,
    name,
    numberProps,
    placeholder,
    type,
    value,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
}) => {
    const getWord = useDictionary();

    const ref = useRef<(HTMLInputElement & HTMLTextAreaElement) | null>(null);

    const isNumberField = type === 'number';

    let InputProps: Partial<InputProps> | undefined;
    if (isSearch) {
        InputProps = {
            classes: {
                input: 'input__search-text',
            },
            startAdornment: <SearchIcon className="input__search-ico" />,
        };
    } else if (description) {
        InputProps = {
            startAdornment: (
                <InputAdornmentComponent lang={lang} data={description} />
            ),
        };
    }

    let popperVariant = variants.common;
    if (multiline) {
        popperVariant = variants.textArea;
    } else if (description) {
        popperVariant = variants.description;
    }

    return (
        <>
            <TextField
                className="input"
                disabled={disabled}
                label={label ? getWord(label) : undefined}
                helperText={helperText ? getWord(helperText) : undefined}
                id={id}
                multiline={multiline}
                name={name}
                type={type ?? 'text'}
                size="small"
                InputLabelProps={
                    isNumberField ? { shrink: isNumberField } : undefined
                }
                InputProps={InputProps}
                FormHelperTextProps={{
                    className: 'input__helper',
                }}
                inputProps={
                    isNumberField
                        ? {
                              min: numberProps?.min,
                              max: numberProps?.max,
                              step: numberProps?.step,
                          }
                        : undefined
                }
                placeholder={placeholder ? getWord(placeholder) : undefined}
                value={value}
                error={error}
                inputRef={ref}
                minRows={multiline ? 5 : undefined}
                onChange={onChange}
                onClick={onClick}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
            />
            <PopperComponent
                message={errorMessage ?? ''}
                lang={lang}
                open={!!error}
                anchorEl={ref.current}
                variant={popperVariant}
            />
        </>
    );
};

const InputComponent = React.memo(React.forwardRef(Component));

export default InputComponent;
